/**
 * Default copy and accordion rows for the DS-01 Strains + Ingredients drawer
 * when Shopify metaobjects are empty or missing.
 */

export type StrainBlendAccordionItem = {
  title: string;
  content: string;
};

export const FALLBACK_MAIN_TITLE = 'Strains + Ingredients';
export const FALLBACK_INTRO_TEXT =
  'DS-01® is formulated with 24 scientifically and clinically studied probiotic strains and a patented, non-fermenting prebiotic.';
export const FALLBACK_SUBTITLE = '53.6 billion AFU. No refrigeration required.';
export const FALLBACK_STRAINS_HEADING = 'Strains';
export const FALLBACK_INNER_CAPSULE_TITLE = 'Probiotic Inner Capsule';
export const FALLBACK_INNER_CAPSULE_DESCRIPTION =
  'Proprietary formulation consisting of 24 probiotic strains (53.6 billion AFU) validated through strain-specific research and tested for all 14 classes of allergens identified by the European Food Safety Authority (EFSA).';
export const FALLBACK_OUTER_CAPSULE_TITLE = 'Prebiotic Outer Capsule';
export const FALLBACK_OUTER_CAPSULE_DESCRIPTION =
  'Our prebiotic formulation consists of punicalagins concentrated from Indian pomegranate—biotransformed by gut microbes into powerful metabolites for human health.';
export const FALLBACK_OTHER_INGREDIENTS_TITLE = 'Other Ingredients';
export const FALLBACK_SAFETY_HEADING = 'Safety + Testing';
export const FALLBACK_SAFETY_DESCRIPTION =
  'DS-01® is held to a superior standard of quality and safety, undergoing 50+ checkpoints throughout the production process testing for purity, potency, and contaminants.';
export const FALLBACK_SUPPLEMENT_FACTS_HEADING = 'Supplement Facts';

export const ALT_STRAINS_SECTION_HERO =
  'DS-01® strains illustration: Daily Synbiotic capsules with inner contents and key probiotic ingredients shown alongside.';
export const ALT_OUTER_CAPSULE_SUPPORTING =
  'Supporting graphic for the prebiotic outer capsule: dual-capsule visual explaining the outer capsule layer of DS-01®.';
export const ALT_SUPPLEMENT_FACTS_PANEL =
  'DS-01® supplement facts label with serving size, AFU counts for probiotic blends, and full ingredient listing.';

export const DEFAULT_STRAIN_BLEND_ITEMS: StrainBlendAccordionItem[] = [
  {
    title: `<span class="accordion-title">Digestive Health Probiotic Blend° <span>— 224mg / 38.7 Billion AFU</span></span>`,
    content: `<uL><li><i>Bifidobacterium breve</i> SD-BR3-IT</li>
                        <li><i>Bifidobacterium longum</i> SD-BB536-JP</li>
                        <li><i>Lacticaseibacillus rhamnosus</i> HRVD113-US</li>
                        <li><i>Lactiplantibacillus plantarum</i> SD-LP1-IT</li>
                        <li><i>Lacticaseibacillus rhamnosus</i> SD-LR6-IT</li>
                        <li><i>Bifidobacterium lactis</i> HRVD524-US</li>
                        <li><i>Bifidobacterium infantis</i> SD-M63-JP</li>
                        <li><i>Bifidobacterium lactis</i> SD-BS5-IT</li>
                        <li><i>Lacticaseibacillus casei</i> HRVD300-US</li>
                        <li><i>Bifidobacterium breve</i> HRVD521-US</li>
                        <li><i>Bifidobacterium lactis</i> SD-150-BE</li>
                        <li><i>Bifidobacterium longum</i> HRVD90b-US</li>
                        <li><i>Lacticaseibacillus rhamnosus</i> SD-GG-BE</li>
                        <li><i>Limosilactobacillus reuteri</i> RD830-FR</li>
                        <li><i>Lactobacillus crispatus</i> SD-LCR01-IT</li>
                        <li><i>Limosilactobacillus fermentum</i> SD-LF8-IT</li></uL>`,
  },
  {
    title: `<span class="accordion-title">Micronutrient Synthesis Probiotic Blend° <span>— 36mg / 7.32 Billion AFU</span></span>`,
    content: `<uL><li><i>Bifidobacterium lactis</i> SD-BA5-IT</li>
                        <li><i>Limosilactobacillus reuteri</i> SD-LRE2-IT</li></uL>`,
  },
  {
    title: `<span class="accordion-title">Dermatological Health Probiotic Blend° <span>— 36mg / 3.34 Billion AFU</span></span>`,
    content: `<uL><li><i>Ligilactobacillus salivarius</i> SD-LS1-IT</li>
                        <li><i>Bifidobacterium longum</i> SD-CECT7347-SP</li>
                        <li><i>Lacticaseibacillus casei</i> SD-CECT9104-SP</li>
                        <li><i>Bifidobacterium lactis</i> SD-CECT8145-SP</li></uL>`,
  },
  {
    title: `<span class="accordion-title">Cardiovascular Health Probiotic Blend° <span>— 13mg / 4.21 Billion AFU</span></span>`,
    content: `<uL><li><i>Lactiplantibacillus plantarum</i> SD-LPLDL-UK</li>
                        <li><i>Bifidobacterium lactis</i> SD-MB2409-IT</li></uL>`,
  },
  {
    title: `<span class="accordion-title">MAPP Microbiota-Accessible Polyphenolic Precursors° <span>— 400mg</span></span>`,
    content: `<p>Indian pomegranate (<i>Punica granatum</i>) extract (fruit) (>40% polyphenolic + phenolic bioactives)</p>`,
  },
];

export const DEFAULT_OTHER_INGREDIENTS_ITEMS: StrainBlendAccordionItem[] = [
  {
    title: 'Other Ingredients',
    content: `<ul><li>Vegan Outer [Hypromellose, Sodium Copper Chlorophyllin Color] and Inner [Hypromellose, Water] Capsules</li>
                      <li>Organic Rice Fiber</li>
                      <li>Organic Rice Extract</li></ul>`,
  },
];
